import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { brand, brandAppName } from '../brand';

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 8,
  padding: '8px 12px',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
};

/**
 * 會員登入表（**兩個 PWA 共用**：主 app `/login` 同理事會端 `/committee`）。
 *
 * ⭐ 產品負責人 2026-10-06 拍板：**只准電話登入**（免密碼）——
 *   · 冇 label、冇解釋字；框內灰字（placeholder）就係唯一指示
 *   · 職員（email ＋ 密碼）走**另一個表**（`StaffLoginForm`，只喺 `/admin` 出現）
 *   · 電話**唔係會員** → **直接跳去登記頁** `/join`（唔顯示任何錯誤／解釋字）
 * ⚠️ `absoluteLinks`：理事會端（第 2 個 PWA）嘅 router 只有 `/committee/*`
 *   → 跳 `/join` 一定要用**真跳頁**（`window.location`），唔可以用 router 跳。
 */
export default function LoginForm({
  onSuccess,
  absoluteLinks = false,
}: {
  onSuccess: () => void;
  absoluteLinks?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  /** 去登記頁（`/join`）；理事會端要真跳頁（router 冇呢條路由） */
  function goJoin() {
    if (absoluteLinks) window.location.assign('/join');
    else nav('/join');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError('');
    const r = await login(phone.trim(), '');
    setBusy(false);
    if (r.ok) {
      onSuccess();
      return;
    }
    /* 唔係會員／未用 WhatsApp 驗證 → 直接去登記（產品負責人指定：唔顯示解釋字） */
    if (r.reason === 'not_member' || r.reason === 'not_verified') {
      goJoin();
      return;
    }
    setError(r.error || t('login_failed'));
  }


  return (
    <form
      onSubmit={onSubmit}
      className="max-w-lg w-full rounded-xl px-8 pb-8 pt-10"
      style={{ background: 'var(--color-card)', boxShadow: 'var(--shadow-soft)' }}
    >
      <img
        src={brand.logos.horizontal}
        alt={brandAppName(i18n.language)}
        style={{ width: '100%', maxWidth: 300, height: 'auto', display: 'block', margin: '0 auto 48px' }}
      />
      {/* 成個表只有一格：框內灰字就係唯一指示（冇 label、冇解釋字） */}
      <input
        type="tel"
        inputMode="tel"
        required
        autoComplete="tel"
        placeholder={t('login_phone_ph')}
        aria-label={t('login_phone_ph')}
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        className="mb-3"
        style={inputStyle}
      />

      {error && (
        <p className="text-sm mb-3" style={{ color: 'var(--color-danger)' }}>
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy}
        className="w-full rounded-lg px-4 py-2 font-semibold"
        style={{ background: 'var(--color-primary)', color: 'var(--color-on-primary)', opacity: busy ? 0.6 : 1 }}
      >
        {busy ? t('login_working') : t('login_submit')}
      </button>

      {absoluteLinks ? (
        <a
          href="/join"
          className="block text-center rounded-lg px-4 py-2 font-semibold mt-4"
          style={{ background: 'var(--color-accent, var(--color-primary))', color: 'var(--color-on-primary)' }}
        >
          {t('join_open_wa')}
        </a>
      ) : (
        <Link
          to="/join"
          className="block text-center rounded-lg px-4 py-2 font-semibold mt-4"
          style={{ background: 'var(--color-accent, var(--color-primary))', color: 'var(--color-on-primary)' }}
        >
          {t('join_open_wa')}
        </Link>
      )}
    </form>
  );
}
